//
// Copyright ArangoDB GmbH, Cologne, Germany
// All rights reserved. See LICENSE.md in the project root for license information.
//

import React, { Component } from "react";
import Auth from "../auth/Auth";
import { RouteComponentProps } from "react-router-dom";
import { Grid, Segment } from "semantic-ui-react";
import { HomeTemplateView, GridRowIntro, TransparentLogo1, TransparentLogo2 } from "./HomeTemplate";
import Markdown from "react-markdown";
import apiClients from "../api/apiclients";
import { DataProcessingAddendum as ApiDataProcessingAddendum } from "../api/lib";
import { ErrorMessage, Loading } from "../ui/lib";
import DocumentTitle from "react-document-title";
import { IWithSignupModalProps, withSignupModal } from "./SignupModalView";

import cloud from "../semantic/themes/arangodb-oasis-default/assets/images/logos/cloud.svg";
import logo from "../semantic/themes/arangodb-oasis-default/assets/images/logos/arangograph-logo-auth.svg";
import { reportError } from "../errors/reporting";
import { Box } from "../ui/_box";

interface IDataProcessingAddendumViewArgs extends IWithSignupModalProps {
  isAuthenticated: boolean;
  onClickLogin: () => void;
  onSignup: () => void;
  onClickLogout: () => void;
  dpa?: ApiDataProcessingAddendum;
  errorMessage?: string;
}

const DataProcessingAddendumView = ({ ...args }: IDataProcessingAddendumViewArgs) => {
  const has_dpa = !!args.dpa;
  const dpa = args.dpa || {};

  return (
    <HomeTemplateView {...args}>
      <Box maxWidth={"960px"} margin={"0 auto"}>
        <Grid columns="16" stackable>
          <GridRowIntro>
            <Grid.Column width="8">
              <a href="/">
                <img src={logo} alt="Arango Managed Platform" />
              </a>
              <h1 className="heading-1">Data Processing Addendum</h1>
            </Grid.Column>
            {false && (
              <Grid.Column width="8" only="computer tablet">
                <TransparentLogo1 src={cloud} />
                <TransparentLogo2 src={cloud} />
                {/*<!-- FullLogo src={cloud} />*/}
              </Grid.Column>
            )}
          </GridRowIntro>
        </Grid>

        <ErrorMessage active={!!args.errorMessage} message={args.errorMessage} />
        {!has_dpa && <Loading message="Loading data processing addendum..." />}

        {has_dpa && (
          <Segment>
            <Markdown
              children={dpa.content || ""}
              components={{
                p: ({ children }) => <p className="para">{children}</p>,
                ol: ({ children }) => <ol className="ordered-list">{children}</ol>,
                ul: ({ children }) => <ul className="unordered-list">{children}</ul>,
                h1: ({ children }) => <h1 className="heading-1">{children}</h1>,
                h2: ({ children }) => <h2 className="heading-2">{children}</h2>,
                h3: ({ children }) => <h3 className="heading-3">{children}</h3>,
                h4: ({ children }) => <h4 className="heading-4">{children}</h4>,
                h5: ({ children }) => <h5 className="heading-5">{children}</h5>,
              }}
            />
          </Segment>
        )}
      </Box>
    </HomeTemplateView>
  );
};

interface IDataProcessingAddendumProps extends RouteComponentProps, IWithSignupModalProps {
  auth: Auth;
}

interface IDataProcessingAddendumState {
  dpa?: ApiDataProcessingAddendum;
  errorMessage?: string;
}

// DataProcessingAddendum shows the current DPA
class DataProcessingAddendum extends Component<IDataProcessingAddendumProps, IDataProcessingAddendumState> {
  state = {
    dpa: undefined,
    errorMessage: undefined,
  } as IDataProcessingAddendumState;

  login = () => {
    this.props.auth.login();
  };
  signup = () => {
    this.props.auth.signup();
  };
  logout = () => {
    this.props.auth.logout();
  };

  reloadDataProcessingAddendum = async () => {
    const req = {};
    const dpa = await apiClients.resourceManagerClient.GetCurrentDataProcessingAddendum(req);
    this.setState({
      dpa: dpa,
      errorMessage: undefined,
    });
  };

  refreshDataProcessingAddendum = () => {
    try {
      this.reloadDataProcessingAddendum();
    } catch (e) {
      this.setState({ errorMessage: e });
      reportError(e);
    }
  };

  componentDidMount() {
    this.refreshDataProcessingAddendum();
  }

  render() {
    return (
      <DocumentTitle title="Data Processing Addendum">
        <DataProcessingAddendumView
          {...this.props}
          {...this.state}
          isAuthenticated={this.props.auth.isAuthenticated()}
          onClickLogin={this.login}
          onSignup={this.signup}
          onClickLogout={this.logout}
        />
      </DocumentTitle>
    );
  }
}

export default withSignupModal(DataProcessingAddendum);
